import moment, { Moment } from 'moment-timezone';
import type { RidershipReadinessIssue } from '../../../types/ridershipReadinessTypes';

export const FISCAL_MONTH_NAMES = [
    'October',
    'November',
    'December',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
] as const;

const DAILY_GAP_CODES = new Set([
    'DailyCsvMissing',
    'DailyNotInDelta',
    'DailyCsvNewerThanDelta',
    'DailySeatCapacityCsvMissing',
]);

export function getCurrentFiscalYearFromLocal(local: Moment): number {
    return local.month() >= 9 ? local.year() + 1 : local.year();
}

export interface CurrentFyReportingScope {
    applies: true;
    excludedMonthName: string;
    dailyCutoffInclusive: string;
}

export function getCurrentFyReportingScope(
    fiscalYear: number,
    timeZoneName: string,
    now?: Moment,
): CurrentFyReportingScope | { applies: false } {
    const local = (now ?? moment()).tz(timeZoneName || 'UTC').locale('en');
    const currentFy = getCurrentFiscalYearFromLocal(local);
    if (fiscalYear !== currentFy) {
        return { applies: false };
    }

    const excludedMonthName = local.format('MMMM');
    const dailyCutoffInclusive = local.clone().startOf('month').subtract(1, 'day').format('YYYY-MM-DD');

    return {
        applies: true,
        excludedMonthName,
        dailyCutoffInclusive,
    };
}

export function isFiscalMonthExcluded(monthName: string, excludedMonthName: string | undefined): boolean {
    if (!excludedMonthName) {
        return false;
    }

    return monthName.localeCompare(excludedMonthName, undefined, { sensitivity: 'accent' }) === 0;
}

function isExcludedMonthEvidence(evidence: string | undefined, excludedMonthName: string): boolean {
    if (!evidence?.trim()) {
        return false;
    }

    const trimmed = evidence.trim();
    if (trimmed.localeCompare(excludedMonthName, undefined, { sensitivity: 'accent' }) === 0) {
        return true;
    }

    const slashIndex = trimmed.indexOf('/');
    if (slashIndex > 0) {
        const monthToken = trimmed.slice(0, slashIndex).trim();
        return monthToken.localeCompare(excludedMonthName, undefined, { sensitivity: 'accent' }) === 0;
    }

    return false;
}

export function shouldExcludeReadinessIssue(
    issue: RidershipReadinessIssue,
    excludedMonthName: string,
    _dailyCutoffInclusive: string,
): boolean {
    if (DAILY_GAP_CODES.has(issue.technicalCode)) {
        return false;
    }

    if (isExcludedMonthEvidence(issue.evidence, excludedMonthName)) {
        return true;
    }

    return issue.plainLanguage.includes(excludedMonthName);
}

export function filterReadinessIssuesForScope(
    issues: RidershipReadinessIssue[],
    fiscalYear: number,
    timeZoneName: string,
    now?: Moment,
): RidershipReadinessIssue[] {
    const scope = getCurrentFyReportingScope(fiscalYear, timeZoneName, now);
    if (!scope.applies) {
        return issues;
    }

    return issues.filter(
        issue => !shouldExcludeReadinessIssue(issue, scope.excludedMonthName, scope.dailyCutoffInclusive),
    );
}
